import {useCallback, useEffect, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {addToast} from '@albert-io/atomic';

import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

import useOnResourceStale from '../hooks/useOnResourceStale';
import {
  useToDoSkillsLazyQuery,
  useAllSkillsLazyQuery,
  useAdaptiveAchievementLazyQuery,
  SkillPathSkillFieldsFragment
} from './StudentSkillPath.queries.generated';
import {
  BadgeCounts,
  DEFAULT_BADGE_COUNTS,
  FILTER_OPTION,
  FilterOption,
  SkillSubjectGroup,
  SkillItem,
  SkillGuide
} from './StudentSkillPath.types';

// Skills are cached per (classroomId, filter)
const skillsCache = new Map<string, SkillSubjectGroup[]>();

// Badges are shared across filters within the same classroom
const badgesCache = new Map<string, BadgeCounts>();

// Call this when leaving the dashboard entirely to force a fresh fetch on return.
export function clearSkillPathCache(): void {
  skillsCache.clear();
  badgesCache.clear();
}

function skillsCacheKey(classroomId: string | undefined, filter: FilterOption): string {
  return `${classroomId ?? ''}:${filter}`;
}

function badgesCacheKey(classroomId: string | undefined): string {
  return classroomId ?? '';
}

function mapSubjectsToSkillGroups(
  subjects: Array<{
    id: string;
    name: string;
    urlSlug: string;
    guides: Array<{
      id: string;
      name: string;
      skills: SkillPathSkillFieldsFragment[];
    }>;
  }>
): SkillSubjectGroup[] {
  return subjects.map((subject) => {
    const skillGuides: SkillGuide[] = subject.guides
      .filter((guide) => guide.skills.length > 0)
      .map((guide) => {
        const items: SkillItem[] = guide.skills.map((gl) => ({
          id: gl.id,
          name: gl.name,
          code: gl.code ?? null,
          myProficiencyLevel: gl.myProficiencyLevel ?? null,
          dueDate: gl.nearestSkillPathDueDate ?? null
        }));
        return {guideId: guide.id, guideName: guide.name, items};
      });
    return {
      subjectId: subject.id,
      subjectName: subject.name,
      browseUrl: `/adaptive-subjects/${subject.urlSlug}`,
      skillGuides
    };
  });
}

function parseBadgeCounts(json: string): BadgeCounts {
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return {
      1: parsed['1'] ?? 0,
      2: parsed['2'] ?? 0,
      3: parsed['3'] ?? 0
    };
  } catch {
    return DEFAULT_BADGE_COUNTS;
  }
}

interface UseStudentSkillPathResult {
  skillSubjectGroups: SkillSubjectGroup[];
  badgeCounts: BadgeCounts;
  isLoading: boolean;
}

export function useStudentSkillPath(
  classroomId: string | undefined,
  filter: FilterOption
): UseStudentSkillPathResult {
  const sKey = skillsCacheKey(classroomId, filter);
  const bKey = badgesCacheKey(classroomId);

  const [activeSkillGroups, setActiveSkillGroups] = useState<SkillSubjectGroup[] | null>(
    () => skillsCache.get(sKey) ?? null
  );
  const [activeBadgeCounts, setActiveBadgeCounts] = useState<BadgeCounts>(
    () => badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS
  );

  // Track the previous (classroomId, filter) to detect navigation changes during render.
  const [prevSKey, setPrevSKey] = useState(() => sKey);
  const [prevBKey, setPrevBKey] = useState(() => bKey);

  if (sKey !== prevSKey) {
    setPrevSKey(sKey);
    setActiveSkillGroups(skillsCache.get(sKey) ?? null);
  }

  if (bKey !== prevBKey) {
    setPrevBKey(bKey);
    setActiveBadgeCounts(badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS);
  }

  const {updateSkillPathLastFetchedAt, studentLastFetchedAt, skillPathLastFetchedAt, classrooms} =
    useStudentDashboard(
      useShallow((state) => ({
        updateSkillPathLastFetchedAt: state.updateSkillPathLastFetchedAt,
        studentLastFetchedAt: state.studentLastFetchedAt,
        skillPathLastFetchedAt: state.skillPathLastFetchedAt,
        classrooms: state.classrooms
      }))
    );

  const classroomIdRef = useRef(classroomId);
  classroomIdRef.current = classroomId;

  const [fetchToDoSkills] = useToDoSkillsLazyQuery();
  const [fetchAllSkills] = useAllSkillsLazyQuery();
  const [fetchAdaptiveAchievement] = useAdaptiveAchievementLazyQuery();

  // When the student is re-fetched, only clear data when on the home page.
  const handleStale = useCallback(() => {
    if (classroomIdRef.current) return;

    skillsCache.clear();
    badgesCache.clear();
    updateSkillPathLastFetchedAt();
    setActiveSkillGroups(null);
    setActiveBadgeCounts(DEFAULT_BADGE_COUNTS);
  }, [updateSkillPathLastFetchedAt]);

  useOnResourceStale(skillPathLastFetchedAt, handleStale);

  useEffect(() => {
    let cancelled = false;

    const skillsNeeded = !skillsCache.has(sKey);
    const badgesNeeded = !badgesCache.has(bKey);

    if (!skillsNeeded && !badgesNeeded) return;

    // On the home tab (no classroomId), use every enrolled classrooms
    const resolvedIds: string[] = classroomId
      ? [classroomId]
      : classrooms.map((c) => c.getId()).toArray();

    if (resolvedIds.length === 0) {
      return;
    }

    const queryVariables = {
      variables: {classroomIds: resolvedIds},
      fetchPolicy: 'network-only'
    } as const;

    const skillsPromise: Promise<SkillSubjectGroup[] | null> = skillsNeeded
      ? filter === FILTER_OPTION.TO_DO
        ? fetchToDoSkills(queryVariables).then((result) => {
            return mapSubjectsToSkillGroups(
              (result.data?.subjects?.results ?? []).map((s) => ({
                id: s.id,
                name: s.name,
                urlSlug: s.urlSlug,
                guides: s.guides.map((g) => ({
                  id: g.id,
                  name: g.name,
                  skills: g.toDoSkills.slice(0, 5)
                }))
              }))
            );
          })
        : fetchAllSkills(queryVariables).then((result) =>
            mapSubjectsToSkillGroups(
              (result.data?.subjects?.results ?? []).map((s) => ({
                id: s.id,
                name: s.name,
                urlSlug: s.urlSlug,
                guides: s.guides.map((g) => ({id: g.id, name: g.name, skills: g.allSkills}))
              }))
            )
          )
      : Promise.resolve(null);

    const badgesPromise: Promise<BadgeCounts | null> = badgesNeeded
      ? fetchAdaptiveAchievement(queryVariables).then((r) =>
          parseBadgeCounts(r.data?.adaptiveAchievement ?? '{}')
        )
      : Promise.resolve(null);

    Promise.all([skillsPromise, badgesPromise])
      .then(([subjectGroups, badgeCounts]) => {
        if (cancelled) return;

        if (subjectGroups !== null) {
          skillsCache.set(sKey, subjectGroups);
          setActiveSkillGroups(subjectGroups);
        }

        if (badgeCounts !== null) {
          badgesCache.set(bKey, badgeCounts);
          setActiveBadgeCounts(badgeCounts);
        }

        updateSkillPathLastFetchedAt();
      })
      .catch(() => {
        if (cancelled) return;
        addToast({
          color: 'negative',
          message: 'Failed to load skill path.'
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    sKey,
    bKey,
    classroomId,
    classrooms,
    filter,
    studentLastFetchedAt,
    fetchToDoSkills,
    fetchAllSkills,
    fetchAdaptiveAchievement,
    updateSkillPathLastFetchedAt
  ]);

  return {
    skillSubjectGroups: activeSkillGroups ?? [],
    badgeCounts: activeBadgeCounts,
    isLoading: activeSkillGroups === null
  };
}
